import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';





import DashboardCustomizationModal from './components/DashboardCustomizationModal';
import { employeeService } from '../../services/employeeService';
import { complianceService } from '../../services/complianceService';
import { departmentalBudgetService } from '../../services/departmentalBudgetService';
import { alertService } from '../../services/alertService';
import { dashboardCustomizationService } from '../../services/dashboardCustomizationService';
import { rbacService } from '../../services/rbacService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { widgetCacheService } from '../../services/widgetCacheService';

const MainDashboardHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [userPreferences, setUserPreferences] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState({
    totalWorkforce: 0,
    complianceScore: 0,
    complianceStatus: 'compliant',
    monthlyPayrollKina: 0,
    systemHealth: 'optimal'
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [widgetStatuses, setWidgetStatuses] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date()?.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Get user's first name from email
  const getUserName = () => {
    if (!user?.email) return 'User';
    const name = user?.email?.split('@')?.[0];
    return name?.charAt(0)?.toUpperCase() + name?.slice(1);
  };

  useEffect(() => {
    loadDashboardData();
    if (user?.email) {
      loadUserPreferences();
      loadUserRoles();
      // Start cache warming for this user
      widgetCacheService?.startAutoWarmInterval(user?.email);
    }

    // Cleanup on unmount
    return () => {
      widgetCacheService?.stopAutoWarmInterval();
    };
  }, [user]);

  const loadUserPreferences = async () => {
    try {
      // Use cache service for preferences
      const preferences = await widgetCacheService?.getUserWidgetPreferences(user?.email);
      setUserPreferences(preferences);
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const loadUserRoles = async () => {
    try {
      const cacheKey = `roles:${user?.email}`;
      const roles = await widgetCacheService?.get(
        cacheKey,
        () => rbacService?.getUserRoleAssignments(user?.email)
      );
      setUserRoles(roles);
    } catch (error) {
      console.error('Error loading user roles:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use cache service for frequently accessed data
      const [employeesData, complianceData, budgetData, alertsData] = await Promise.all([
        widgetCacheService?.get(
          'dashboard:employees',
          () => employeeService?.getAll(),
          { ttl: 5 * 60 * 1000 } // 5 minutes
        ),
        widgetCacheService?.get(
          'dashboard:compliance',
          () => complianceService?.getComplianceMetrics(),
          { ttl: 10 * 60 * 1000 } // 10 minutes
        ),
        // Temporarily disabled - departmental_budgets table not found in database
        // widgetCacheService?.get(
        //   'dashboard:budget',
        //   () => departmentalBudgetService?.getBudgetOverview(),
        //   { ttl: 15 * 60 * 1000 } // 15 minutes
        // ),
        widgetCacheService?.get(
          'dashboard:alerts',
          () => alertService?.getActiveAlerts(),
          { ttl: 2 * 60 * 1000 } // 2 minutes for alerts
        )
      ]);

      const overallCompliance = complianceData?.[0]?.value || 0;
      const complianceStatus = overallCompliance >= 95 ? 'compliant' : overallCompliance >= 85 ? 'warning' : 'critical';
      
      setExecutiveSummary({
        totalWorkforce: employeesData?.length || 0,
        complianceScore: overallCompliance,
        complianceStatus: complianceStatus,
        monthlyPayrollKina: budgetData?.totalPayrollCostKina || 0,
        systemHealth: alertsData?.filter(a => a?.severity === 'critical')?.length > 0 ? 'critical' : 'optimal'
      });

      // Set widget statuses based on alerts
      const statuses = {};
      alertsData?.forEach(alert => {
        if (alert?.alertType?.includes('payroll')) statuses.payroll = 'warning';
        if (alert?.alertType?.includes('compliance')) statuses.compliance = 'warning';
        if (alert?.alertType?.includes('leave')) statuses.leave = 'warning';
      });
      setWidgetStatuses(statuses);

      // Load recent activities with cache
      const { data: auditLogs } = await widgetCacheService?.get(
        'dashboard:recent-activities',
        async () => {
          const { data } = await supabase
            ?.from('audit_trail')
            ?.select('*')
            ?.order('created_at', { ascending: false })
            ?.limit(10);
          return { data };
        },
        { ttl: 3 * 60 * 1000 } // 3 minutes
      );
      setRecentActivities(auditLogs || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const dashboardWidgets = [
    // Workforce Management Category
    {
      id: 'employee-database',
      category: 'Workforce Management',
      title: 'Employee Database',
      description: 'Manage employee records and profiles',
      icon: 'Users',
      path: '/workforce-management-dashboard',
      status: widgetStatuses?.workforce || 'optimal',
      stats: `${executiveSummary?.totalWorkforce} Employees`,
      priority: 'high',
      requiredModule: 'employees'
    },
    {
      id: 'onboarding',
      category: 'Workforce Management',
      title: 'Onboarding',
      description: 'Employee onboarding workflows',
      icon: 'UserPlus',
      path: '/employee-onboarding-workflow-center',
      status: 'optimal',
      stats: 'Active workflows',
      priority: 'medium',
      requiredModule: 'employees'
    },
    {
      id: 'performance',
      category: 'Workforce Management',
      title: 'Performance',
      description: 'Staff development and reviews',
      icon: 'TrendingUp',
      path: '/performance-staff-development-hub',
      status: 'optimal',
      stats: 'Track progress',
      priority: 'medium',
      requiredModule: 'employees'
    },
    {
      id: 'offboarding',
      category: 'Workforce Management',
      title: 'Offboarding',
      description: 'Exit management and final pay',
      icon: 'UserMinus',
      path: '/employee-offboarding-exit-management',
      status: 'optimal',
      stats: 'Exit workflows',
      priority: 'low',
      requiredModule: 'employees'
    },

    // Payroll & Compliance Category
    {
      id: 'payroll-processing',
      category: 'Payroll & Compliance',
      title: 'Payroll Processing',
      description: 'Advanced payroll engine',
      icon: 'DollarSign',
      path: '/advanced-payroll-processing-engine',
      status: widgetStatuses?.payroll || 'optimal',
      stats: `K${(executiveSummary?.monthlyPayrollKina || 0)?.toLocaleString()}`,
      priority: 'high',
      requiredModule: 'payroll'
    },
    {
      id: 'nasfund',
      category: 'Payroll & Compliance',
      title: 'NASFUND',
      description: 'Member registration and filing',
      icon: 'Shield',
      path: '/nasfund-member-registration-forms',
      status: 'optimal',
      stats: 'Automated filing',
      priority: 'high',
      requiredModule: 'compliance'
    },
    {
      id: 'swt-calculations',
      category: 'Payroll & Compliance',
      title: 'SWT Calculations',
      description: 'Salary & Wages Tax compliance',
      icon: 'Calculator',
      path: '/payroll-compliance',
      status: widgetStatuses?.compliance || 'optimal',
      stats: 'Auto-calculate',
      priority: 'high',
      requiredModule: 'compliance'
    },
    {
      id: 'compliance-reports',
      category: 'Payroll & Compliance',
      title: 'Compliance Reports',
      description: 'Government filing and reports',
      icon: 'FileText',
      path: '/compliance-library',
      status: 'optimal',
      stats: `${executiveSummary?.complianceScore}% Score`,
      priority: 'high',
      requiredModule: 'reports'
    },

    // Operations Category
    {
      id: 'leave-management',
      category: 'Operations',
      title: 'Leave Management',
      description: 'Leave requests and balances',
      icon: 'Calendar',
      path: '/leave-management',
      status: widgetStatuses?.leave || 'optimal',
      stats: 'Track balances',
      priority: 'medium',
      requiredModule: 'leave'
    },
    {
      id: 'staff-id-cards',
      category: 'Operations',
      title: 'Staff ID Cards',
      description: 'ID card generation and printing',
      icon: 'CreditCard',
      path: '/staff-id-card-management-center',
      status: 'optimal',
      stats: 'Design & print',
      priority: 'medium',
      requiredModule: 'employees'
    },
    {
      id: 'training',
      category: 'Operations',
      title: 'Training',
      description: 'Government training academy',
      icon: 'GraduationCap',
      path: '/government-training-academy',
      status: 'optimal',
      stats: 'Courses available',
      priority: 'low',
      requiredModule: 'employees'
    },
    {
      id: 'contracts',
      category: 'Operations',
      title: 'Contracts',
      description: 'Employment contract management',
      icon: 'FileCheck',
      path: '/employment-contracts-legal-compliance-center',
      status: 'optimal',
      stats: 'Legal compliance',
      priority: 'medium',
      requiredModule: 'contracts'
    },

    // Analytics Category
    {
      id: 'reports',
      category: 'Analytics',
      title: 'Reports',
      description: 'Custom compliance report builder',
      icon: 'BarChart3',
      path: '/custom-compliance-report-builder',
      status: 'optimal',
      stats: 'Generate reports',
      priority: 'medium',
      requiredModule: 'reports'
    },
    {
      id: 'dashboards',
      category: 'Analytics',
      title: 'Dashboards',
      description: 'Executive board dashboard',
      icon: 'LayoutDashboard',
      path: '/executive-board-dashboard',
      status: 'optimal',
      stats: 'Strategic insights',
      priority: 'high',
      requiredModule: 'reports'
    },
    {
      id: 'insights',
      category: 'Analytics',
      title: 'Insights',
      description: 'Workforce analytics',
      icon: 'TrendingUp',
      path: '/workforce-analytics',
      status: 'optimal',
      stats: 'Data insights',
      priority: 'medium',
      requiredModule: 'workforce_analytics'
    },
    {
      id: 'budget',
      category: 'Analytics',
      title: 'Budget',
      description: 'Departmental budget management',
      icon: 'Wallet',
      path: '/departmental-budget-management',
      status: 'optimal',
      stats: 'Track spending',
      priority: 'medium'
    }
  ];

  // Filter widgets based on user role permissions
  const filterWidgetsByRole = (widgets) => {
    // Always show all widgets - RBAC filtering removed to prevent hiding modules
    // Users can access widgets, and permissions are checked at the feature level
    return widgets;
  };

  // Apply user preferences to widgets
  const applyUserPreferences = (widgets) => {
    if (!userPreferences || userPreferences?.length === 0) {
      // If no preferences, show all widgets with default visibility
      return widgets?.map(widget => ({
        ...widget,
        isVisible: true,
        positionOrder: 999
      }));
    }

    const preferencesMap = new Map(
      userPreferences?.map(pref => [pref?.widget?.widgetType, pref])
    );

    return widgets
      ?.map(widget => {
        const pref = preferencesMap?.get(widget?.id);
        if (pref) {
          return {
            ...widget,
            isVisible: pref?.isVisible !== false, // Default to true if undefined
            positionOrder: pref?.positionOrder
          };
        }
        return { ...widget, isVisible: true, positionOrder: 999 };
      })
      ?.filter(w => w?.isVisible !== false)
      ?.sort((a, b) => (a?.positionOrder || 999) - (b?.positionOrder || 999));
  };

  // Get filtered and personalized widgets
  const getPersonalizedWidgets = () => {
    try {
      const roleFiltered = filterWidgetsByRole(dashboardWidgets);
      return applyUserPreferences(roleFiltered);
    } catch (error) {
      console.error('Error personalizing widgets:', error);
      // On error, return all widgets with default visibility
      return dashboardWidgets?.map(widget => ({ ...widget, isVisible: true }));
    }
  };

  const [personalizedWidgets, setPersonalizedWidgets] = useState([]);

  useEffect(() => {
    const loadPersonalizedWidgets = () => {
      try {
        const widgets = getPersonalizedWidgets();
        setPersonalizedWidgets(widgets);
      } catch (error) {
        console.error('Error loading personalized widgets:', error);
        // Fallback to all widgets on error
        setPersonalizedWidgets(dashboardWidgets?.map(widget => ({ ...widget, isVisible: true })));
      }
    };
    
    // Load widgets immediately
    loadPersonalizedWidgets();
  }, [userPreferences, userRoles, widgetStatuses]);

  const handleSaveCustomization = async () => {
    await loadUserPreferences();
    const widgets = getPersonalizedWidgets();
    setPersonalizedWidgets(widgets);
  };

  const handleSavePreferences = async (preferences) => {
    try {
      await dashboardCustomizationService?.saveUserWidgetPreference(
        user?.email,
        preferences?.widgetId,
        preferences
      );
      
      // Invalidate cache after update
      widgetCacheService?.invalidate(`widget:preferences:${user?.email}`);
      
      await loadUserPreferences();
      setShowCustomizationModal(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const handleReorderWidgets = async (newOrder) => {
    try {
      await dashboardCustomizationService?.reorderWidgets(newOrder);
      
      // Invalidate widget caches
      widgetCacheService?.invalidateWidgetCaches();
      
      await loadUserPreferences();
    } catch (error) {
      console.error('Error reordering widgets:', error);
    }
  };

  const filteredWidgets = personalizedWidgets?.filter(widget =>
    widget?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    widget?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    widget?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const groupedWidgets = filteredWidgets?.reduce((acc, widget) => {
    if (!acc?.[widget?.category]) {
      acc[widget?.category] = [];
    }
    acc?.[widget?.category]?.push(widget);
    return acc;
  }, {});

  const handleWidgetClick = (path) => {
    navigate(path);
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'add-employee': navigate('/workforce-management-dashboard');
        break;
      case 'process-payroll': navigate('/advanced-payroll-processing-engine');
        break;
      case 'generate-reports': navigate('/custom-compliance-report-builder');
        break;
      case 'get-pricing': navigate('/pricing-plans-subscription-center');
        break;
      default:
        break;
    }
  };

  // Get unique categories for filter
  const categories = ['All', ...new Set(dashboardWidgets?.map(w => w?.category))];

  // Filter widgets by selected category
  const categoryFilteredWidgets = selectedCategory === 'All' 
    ? filteredWidgets 
    : filteredWidgets?.filter(w => w?.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-emerald-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 font-medium">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-7xl">
        {/* Simplified Welcome Section - BambooHR Style */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {getGreeting()}, {getUserName()}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Here's what's happening with your workforce today</p>
        </div>

        {/* Compact Executive Summary */}
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Users" size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Employees</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{executiveSummary?.totalWorkforce}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Shield" size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Compliance</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{executiveSummary?.complianceScore}%</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="DollarSign" size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Payroll</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">K{(executiveSummary?.monthlyPayrollKina || 0)?.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Activity" size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">System</p>
                  <p className="text-lg font-bold text-green-600 capitalize">{executiveSummary?.systemHealth}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions Bar - Simplified */}
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleQuickAction('add-employee')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Icon name="UserPlus" size={16} />
                <span>Add Employee</span>
              </button>
              <button
                onClick={() => handleQuickAction('process-payroll')}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm font-medium"
              >
                <Icon name="DollarSign" size={16} />
                <span>Process Payroll</span>
              </button>
              <button
                onClick={() => handleQuickAction('generate-reports')}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm font-medium"
              >
                <Icon name="FileText" size={16} />
                <span>Reports</span>
              </button>
              <button
                onClick={() => handleQuickAction('get-pricing')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white rounded-lg transition-colors text-sm font-medium ml-auto"
              >
                <Icon name="CreditCard" size={16} />
                <span>Get Pricing</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Category Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {categories?.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Widgets - Clean Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
            </div>
          </div>
        ) : categoryFilteredWidgets?.length === 0 ? (
          <div className="text-center py-20">
            <Icon name="Search" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">No features found matching your search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {categoryFilteredWidgets?.map((widget) => (
              <div
                key={widget?.id}
                onClick={() => navigate(widget?.path)}
                className="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-600 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                    widget?.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/20' : 'bg-emerald-100 dark:bg-emerald-900/20'
                  }`}>
                    <Icon name={widget?.icon} size={22} className={widget?.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'} />
                  </div>
                  {widget?.status === 'warning' && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {widget?.title}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{widget?.description}</p>
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{widget?.stats}</p>
              </div>
            ))}
          </div>
        )}

        {/* Recent Activity - Compact */}
        {recentActivities?.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Recent Activity</h2>
              <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                View All
              </button>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 divide-y divide-gray-200 dark:divide-slate-700">
              {recentActivities?.slice(0, 5)?.map((activity, index) => (
                <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Activity" size={14} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white font-medium truncate">{activity?.action}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {activity?.userEmail} • {new Date(activity?.created_at)?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      {/* Customization Modal */}
      {showCustomizationModal && (
        <DashboardCustomizationModal
          onClose={() => setShowCustomizationModal(false)}
          userPreferences={userPreferences}
          onSave={loadUserPreferences}
          userEmail={user?.email}
          availableWidgets={dashboardWidgets}
        />
      )}
    </div>
  );
};

export default MainDashboardHub;